import React, { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@root/hooks/useQuery';
import { axiosHelper } from '@root/services/axios';

const ZendeskLogin = () => {
  const query = useQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const submitToZendesk = (jwtToken, returnTo) => {
    try {
      console.log('Submitting JWT to Zendesk:', { jwtToken: jwtToken.substring(0, 50) + '...', returnTo });

      // Create and submit form (required by Zendesk)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://seclogicinc.zendesk.com/access/jwt';
      form.style.display = 'none';
      form.target = '_blank'; // Open in new tab

      // Add JWT token
      const jwtInput = document.createElement('input');
      jwtInput.type = 'hidden';
      jwtInput.name = 'jwt';
      jwtInput.value = jwtToken;
      form.appendChild(jwtInput);

      // Add return_to if provided
      if (returnTo) {
        const returnToInput = document.createElement('input');
        returnToInput.type = 'hidden';
        returnToInput.name = 'return_to';
        returnToInput.value = returnTo;
        form.appendChild(returnToInput);
      }

      document.body.appendChild(form);

      console.log('Submitting form to Zendesk...');
      form.submit();
    } catch (error) {
      console.error('Form submission error:', error);
      setError('Failed to submit authentication to Zendesk');
      setIsLoading(false);
    }
  };

  const handleZendeskSSO = useCallback(async returnTo => {
    try {
      // Get current user data from localStorage or context
      const userInfo = JSON.parse(localStorage.getItem('User') || '{}');
      const tenantName = localStorage.getItem('tenantName');
      console.log('userInfo', userInfo);
      const userData = {
        id: userInfo.user.id || userInfo.email,
        name: userInfo.user.name || 'User',
        email: userInfo.user.email,
        role: userInfo.user.role || 'end_user',
        organization: tenantName || 'seclogicinc',
        tags: ['cspm_user', 'support_request'],
      };

      console.log('Calling Zendesk SSO API with user data:', userData);

      // Call the Zendesk SSO API
      const response = await axiosHelper.axiosPost(
        'zendesk-sso',
        {
          user_data: userData,
          return_to: returnTo,
        },
        true,
      );

      console.log('Zendesk SSO API response:', response);

      if (response?.error || !response?.data) {
        throw new Error(response?.msg || 'Failed to generate Zendesk SSO data');
      }

      // Submit JWT to Zendesk
      submitToZendesk(response.data.data.jwt_token, returnTo);
    } catch (error) {
      console.error('Zendesk SSO Error:', error);
      setError(`Failed to authenticate with Zendesk: ${error.message}`);
      setIsLoading(false);
    }
  }, []);

  const handleZendeskRedirect = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get Zendesk parameters
      const returnTo = query.get('return_to');
      const brandId = query.get('brand_id');
      const localeId = query.get('locale_id');
      const timestamp = query.get('timestamp');

      console.log('Zendesk redirect parameters:', { returnTo, brandId, localeId, timestamp });

      // Check if user is already logged in to your app
      const accessToken = localStorage.getItem('_t');

      if (!accessToken) {
        // User needs to log in first
        console.log('User not logged in, redirecting to login page');
        const loginUrl = `/login?redirect=zendesk&return_to=${encodeURIComponent(returnTo || '')}`;
        window.location.href = loginUrl;
        return;
      }

      // User is logged in, proceed with SSO
      console.log('User is logged in, proceeding with Zendesk SSO');
      await handleZendeskSSO(returnTo);
    } catch (error) {
      console.error('Zendesk redirect error:', error);
      setError('Failed to process Zendesk authentication');
      setIsLoading(false);
    }
  }, [query, handleZendeskSSO]);

  useEffect(() => {
    handleZendeskRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleZendeskRedirect]);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}>
        <h2 style={{ color: '#e53e3e', marginBottom: '20px' }}>Authentication Error</h2>
        <p style={{ color: '#4a5568', marginBottom: '20px' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1e293b',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}>
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3182ce',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px',
          }}></div>
        <h2 style={{ color: '#2d3748', marginBottom: '10px' }}>Authenticating with Zendesk...</h2>
        <p style={{ color: '#718096' }}>Please wait while we process your request.</p>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default ZendeskLogin;
